import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUpRight,
  Bell,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Clock3,
  ExternalLink,
  GripVertical,
  Layers3,
  MapPin,
  Plus,
  Repeat2,
  Search,
  SlidersHorizontal,
  TimerReset,
  Users,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { Badge, deliveryTone, rsvpLabel, rsvpTone } from '../ui/Badge';
import { Modal } from '../ui/Modal';
import { useToast } from '../ui/Toast';

type ViewMode = 'month' | 'week' | 'day' | 'agenda';

interface Calendar {
  id: string;
  name: string;
  color: string;
  timezone: string;
  accessRole?: 'OWNER' | 'WRITE' | 'READ';
}

interface EventRow {
  id: string;
  occurrenceId?: string;
  summary: string;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  location?: string | null;
  status: string;
  rrule?: string | null;
  calendar: Calendar;
  attendees?: AttendeePreview[];
}

interface AttendeeDraft { email: string; name?: string }
interface AttendeePreview {
  id: string;
  email: string;
  name?: string | null;
  rsvp: string;
  rsvpAt?: string | null;
  sendCount?: number;
  lastStatus?: string | null;
}

interface ReminderPreview {
  id: string;
  minutesBefore: number;
  channel: 'NOTIFICATION' | 'EMAIL';
  sentAt?: string | null;
}

interface DeliveryPreview {
  id: string;
  toEmail: string;
  status: string;
  method: string;
  queuedAt: string;
  sentAt?: string | null;
  failedAt?: string | null;
}

interface EventPreview extends EventRow {
  uid: string;
  sequence: number;
  description?: string | null;
  timezone: string;
  attendees: AttendeePreview[];
  reminders: ReminderPreview[];
  deliveries: DeliveryPreview[];
}

type RecurrenceFrequency = 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
type RecurrenceEndMode = 'NEVER' | 'COUNT' | 'UNTIL';
type RecurrenceDateMode = 'DATE' | 'WEEKDAY';
const weekdayOptions = [
  { value: 'MO', label: 'Mo' },
  { value: 'TU', label: 'Di' },
  { value: 'WE', label: 'Mi' },
  { value: 'TH', label: 'Do' },
  { value: 'FR', label: 'Fr' },
  { value: 'SA', label: 'Sa' },
  { value: 'SU', label: 'So' },
];
const ordinalOptions = [
  { value: '1', label: 'erster' },
  { value: '2', label: 'zweiter' },
  { value: '3', label: 'dritter' },
  { value: '4', label: 'vierter' },
  { value: '-1', label: 'letzter' },
];
const monthOptions = [
  'Januar',
  'Februar',
  'Maerz',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
].map((label, index) => ({ value: String(index + 1), label }));

interface QuickForm {
  calendarId: string;
  summary: string;
  location: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  allDay: boolean;
  recurrence: RecurrenceFrequency;
  recurrenceInterval: string;
  recurrenceByDay: string[];
  recurrenceMonthlyMode: RecurrenceDateMode;
  recurrenceYearlyMode: RecurrenceDateMode;
  recurrenceMonthDay: string;
  recurrenceBySetPos: string;
  recurrenceByWeekday: string;
  recurrenceByMonth: string;
  recurrenceEndMode: RecurrenceEndMode;
  recurrenceCount: string;
  recurrenceUntil: string;
  sendInvitations: boolean;
}

export function CalendarViewPage() {
  const [mode, setMode] = useState<ViewMode>('month');
  const [anchor, setAnchor] = useState(startOfDay(new Date()));
  const [events, setEvents] = useState<EventRow[]>([]);
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [visibleCalendars, setVisibleCalendars] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickForm, setQuickForm] = useState(() => initialQuickForm(new Date()));
  const [attendeesInput, setAttendeesInput] = useState('');
  const [remindersInput, setRemindersInput] = useState('15:NOTIFICATION');
  const [draggingEventId, setDraggingEventId] = useState<string | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<EventPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const touchStartX = useRef<number | null>(null);
  const toast = useToast();
  const range = useMemo(() => visibleRange(anchor, mode), [anchor, mode]);

  const writableCalendars = calendars.filter((c) => c.accessRole !== 'READ');
  const filteredEvents = useMemo(() => {
    const q = query.trim().toLowerCase();
    return events.filter((event) => {
      const calendarVisible = visibleCalendars.size === 0 || visibleCalendars.has(event.calendar.id);
      const textVisible =
        !q ||
        event.summary.toLowerCase().includes(q) ||
        event.location?.toLowerCase().includes(q) ||
        event.calendar.name.toLowerCase().includes(q);
      return calendarVisible && textVisible;
    });
  }, [events, query, visibleCalendars]);

  const loadEvents = async () => {
    setEvents(await api.get<EventRow[]>(`/api/events?from=${range.from.toISOString()}&to=${range.to.toISOString()}`));
  };

  useEffect(() => {
    Promise.all([api.get<Calendar[]>('/api/calendars'), loadEvents()]).then(([cals]) => {
      setCalendars(cals);
      if (visibleCalendars.size === 0) setVisibleCalendars(new Set(cals.map((c) => c.id)));
      if (cals[0]) setQuickForm((f) => ({ ...f, calendarId: f.calendarId || cals[0].id }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from.toISOString(), range.to.toISOString()]);

  useEffect(() => {
    if (!selectedEventId) {
      setSelectedEvent(null);
      return;
    }
    setPreviewLoading(true);
    api.get<EventPreview>(`/api/events/${selectedEventId}`)
      .then(setSelectedEvent)
      .catch((err) => toast.error(err instanceof ApiError ? err.message : 'Termin konnte nicht geladen werden'))
      .finally(() => setPreviewLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventId]);

  const title = titleForRange(anchor, range, mode);
  const upcoming = filteredEvents.filter((event) => new Date(event.endsAt).getTime() >= Date.now()).slice(0, 6);
  const todayEvents = eventsForDay(filteredEvents, new Date());
  const activeCalendars = calendars.filter((calendar) => visibleCalendars.has(calendar.id)).length;
  const busiest = busiestDay(daysBetween(range.from, range.to), filteredEvents);
  const visibleHours = totalDurationHours(filteredEvents);

  const openCreate = (date = anchor, hour = 9) => {
    const start = new Date(date);
    start.setHours(hour, 0, 0, 0);
    setQuickForm({ ...initialQuickForm(start), calendarId: writableCalendars[0]?.id ?? calendars[0]?.id ?? '' });
    setAttendeesInput('');
    setRemindersInput('15:NOTIFICATION');
    setQuickOpen(true);
  };

  const openCreateRange = (from: Date, toInclusive: Date) => {
    const [rangeStart, rangeEnd] = orderedDays(from, toInclusive);
    const start = startOfDay(rangeStart);
    const end = addDays(startOfDay(rangeEnd), 1);
    setQuickForm({
      ...initialQuickForm(start),
      calendarId: writableCalendars[0]?.id ?? calendars[0]?.id ?? '',
      startsAt: toLocalInput(start),
      endsAt: toLocalInput(end),
      allDay: true,
    });
    setAttendeesInput('');
    setRemindersInput('15:NOTIFICATION');
    setQuickOpen(true);
  };

  const openCreateTimedRange = (startsAt: Date, endsAt: Date) => {
    const end = endsAt.getTime() > startsAt.getTime() ? endsAt : addMinutes(startsAt, 60);
    setQuickForm({
      ...initialQuickForm(startsAt),
      calendarId: writableCalendars[0]?.id ?? calendars[0]?.id ?? '',
      startsAt: toLocalInput(startsAt),
      endsAt: toLocalInput(end),
      allDay: false,
    });
    setAttendeesInput('');
    setRemindersInput('15:NOTIFICATION');
    setQuickOpen(true);
  };

  const createEvent = async () => {
    try {
      const attendees: AttendeeDraft[] = parseAttendees(attendeesInput);
      await api.post('/api/events', {
        ...quickForm,
        startsAt: new Date(quickForm.startsAt).toISOString(),
        endsAt: new Date(quickForm.endsAt).toISOString(),
        rrule: recurrenceToRrule(quickForm),
        attendees,
        reminders: parseReminders(remindersInput),
      });
      toast.success(attendees.length > 0 ? 'Termin angelegt und Einladungen eingereiht' : 'Termin angelegt');
      setQuickOpen(false);
      await loadEvents();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Termin konnte nicht angelegt werden');
    }
  };

  const moveEvent = async (event: EventRow, targetDate: Date, targetHour?: number, targetMinute?: number) => {
    if (event.rrule) {
      toast.info('Serientermine werden im Detail bearbeitet.');
      return;
    }
    const calendar = calendars.find((cal) => cal.id === event.calendar.id);
    if (calendar?.accessRole === 'READ') {
      toast.error('Dieser Kalender ist nur lesbar.');
      return;
    }
    const currentStart = new Date(event.startsAt);
    const currentEnd = new Date(event.endsAt);
    const duration = currentEnd.getTime() - currentStart.getTime();
    const nextStart = new Date(targetDate);
    if (targetHour !== undefined) {
      nextStart.setHours(targetHour, targetMinute ?? currentStart.getMinutes(), 0, 0);
    } else {
      nextStart.setHours(currentStart.getHours(), currentStart.getMinutes(), 0, 0);
    }
    const nextEnd = new Date(nextStart.getTime() + duration);
    try {
      await api.patch(`/api/events/${event.id}`, {
        startsAt: nextStart.toISOString(),
        endsAt: nextEnd.toISOString(),
        sendInvitations: false,
      });
      toast.success('Termin verschoben');
      await loadEvents();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Termin konnte nicht verschoben werden');
    } finally {
      setDraggingEventId(null);
    }
  };

  const resizeEvent = async (event: EventRow, startsAt: Date, endsAt: Date) => {
    if (event.rrule) {
      toast.info('Serientermine werden im Detail bearbeitet.');
      return;
    }
    const calendar = calendars.find((cal) => cal.id === event.calendar.id);
    if (calendar?.accessRole === 'READ') {
      toast.error('Dieser Kalender ist nur lesbar.');
      return;
    }
    if (endsAt.getTime() <= startsAt.getTime()) {
      toast.error('Das Ende muss nach dem Start liegen.');
      return;
    }
    try {
      await api.patch(`/api/events/${event.id}`, {
        startsAt: startsAt.toISOString(),
        endsAt: endsAt.toISOString(),
        sendInvitations: false,
      });
      toast.success('Termindauer angepasst');
      await loadEvents();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Termin konnte nicht angepasst werden');
    }
  };

  const draggedEvent = () => filteredEvents.find((event) => event.id === draggingEventId) ?? null;

  const resendFromPreview = async (method: 'REQUEST' | 'REMINDER' | 'CANCEL') => {
    if (!selectedEvent) return;
    try {
      await api.post(`/api/events/${selectedEvent.id}/invitations`, { method });
      toast.success(method === 'REMINDER' ? 'Erinnerung eingereiht' : method === 'CANCEL' ? 'Absage eingereiht' : 'Einladung eingereiht');
      setSelectedEvent(await api.get<EventPreview>(`/api/events/${selectedEvent.id}`));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Versand konnte nicht gestartet werden');
    }
  };

  const duplicateFromPreview = async () => {
    if (!selectedEvent) return;
    try {
      const created = await api.post<{ event: EventPreview }>('/api/events', {
        calendarId: selectedEvent.calendar.id,
        summary: `${selectedEvent.summary} (Kopie)`,
        description: selectedEvent.description ?? undefined,
        location: selectedEvent.location ?? undefined,
        startsAt: selectedEvent.startsAt,
        endsAt: selectedEvent.endsAt,
        allDay: selectedEvent.allDay,
        timezone: selectedEvent.timezone,
        rrule: selectedEvent.rrule ?? undefined,
        attendees: [],
        reminders: selectedEvent.reminders.map((reminder) => ({
          minutesBefore: reminder.minutesBefore,
          channel: reminder.channel,
        })),
        sendInvitations: false,
      });
      toast.success('Termin dupliziert');
      setSelectedEventId(created.event.id);
      await loadEvents();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Termin konnte nicht dupliziert werden');
    }
  };

  const cancelFromPreview = async () => {
    if (!selectedEvent) return;
    try {
      const updated = await api.patch<{ event: EventPreview }>(`/api/events/${selectedEvent.id}`, {
        status: 'CANCELLED',
        sendInvitations: true,
      });
      toast.success(selectedEvent.attendees.length > 0 ? 'Termin abgesagt und Absagen eingereiht' : 'Termin abgesagt');
      setSelectedEvent(updated.event);
      await loadEvents();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Termin konnte nicht abgesagt werden');
    }
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const editing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName ?? '') || target?.isContentEditable;
      if (quickOpen) return;
      if (event.key === '/' && !editing) {
        event.preventDefault();
        searchRef.current?.focus();
      }
      if (editing) return;
      if (event.key === 'ArrowLeft') setAnchor((current) => shift(current, mode, -1));
      if (event.key === 'ArrowRight') setAnchor((current) => shift(current, mode, 1));
      if (event.key.toLowerCase() === 't') setAnchor(startOfDay(new Date()));
      if (event.key.toLowerCase() === 'n') openCreate(new Date());
      if (event.key.toLowerCase() === 'm') setMode('month');
      if (event.key.toLowerCase() === 'w') setMode('week');
      if (event.key.toLowerCase() === 'd') setMode('day');
      if (event.key.toLowerCase() === 'a') setMode('agenda');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, quickOpen, filteredEvents]);

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    const delta = (event.changedTouches[0]?.clientX ?? touchStartX.current) - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 80) return;
    setAnchor((current) => shift(current, mode, delta > 0 ? -1 : 1));
  };

  return (
    <div className="flex flex-col gap-4" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      <section className="relative overflow-hidden rounded-xl border border-border bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.025))] shadow-card">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-primary/60 to-transparent" />
        <div className="flex flex-col gap-5 p-4 sm:p-5 xl:flex-row xl:items-end xl:justify-between">
          <div className="min-w-0">
            <div className="mb-3 inline-flex items-center gap-2 rounded-md border border-border bg-bg/50 px-3 py-1.5 text-small text-ink-secondary">
              <CalendarDays size={15} />
              {modeLabel(mode)}
            </div>
            <h1 className="text-h2 sm:text-h1 tracking-normal">{title}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-small text-ink-muted">
              <span className="inline-flex items-center gap-1.5"><Clock3 size={14} />{filteredEvents.length} Termine im Blick</span>
              <span>·</span>
              <span>{todayEvents.length} heute</span>
              <span>·</span>
              <span>{activeCalendars}/{calendars.length || 0} Kalender aktiv</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-md border border-border bg-bg/45 p-1">
              <button className="btn btn-ghost !min-h-0 !px-2.5 !py-2" title="Vorheriger Zeitraum" onClick={() => setAnchor(shift(anchor, mode, -1))}><ChevronLeft size={17} /></button>
              <button className="btn btn-secondary !min-h-0 !py-2" onClick={() => setAnchor(startOfDay(new Date()))}>Heute</button>
              <button className="btn btn-ghost !min-h-0 !px-2.5 !py-2" title="Naechster Zeitraum" onClick={() => setAnchor(shift(anchor, mode, 1))}><ChevronRight size={17} /></button>
            </div>
            <div className="flex rounded-md border border-border bg-bg/45 p-1">
              {(['month', 'week', 'day', 'agenda'] as ViewMode[]).map((item) => (
                <button key={item} className={`px-3 py-2 rounded text-small transition-colors ${mode === item ? 'bg-white/12 text-ink-primary shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]' : 'text-ink-secondary hover:text-ink-primary hover:bg-white/5'}`} onClick={() => setMode(item)}>
                  {modeLabel(item)}
                </button>
              ))}
            </div>
            <button className="btn btn-primary" onClick={() => openCreate(new Date())}><Plus size={17} />Termin</button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 border-t border-border/70 bg-bg/20 p-3 sm:grid-cols-4 sm:p-4">
          <HeroMetric icon={Clock3} label="Heute" value={`${todayEvents.length}`} detail={todayEvents.length === 1 ? 'Termin' : 'Termine'} />
          <HeroMetric icon={ArrowUpRight} label="Naechster" value={upcoming[0] ? relativeDay(upcoming[0].startsAt) : 'frei'} detail={upcoming[0] ? timeRange(upcoming[0]) : 'kein Eintrag'} />
          <HeroMetric icon={TimerReset} label="Auslastung" value={`${visibleHours} h`} detail="sichtbarer Zeitraum" />
          <HeroMetric icon={Layers3} label="Peak" value={busiest.label} detail={busiest.count === 1 ? '1 Termin' : `${busiest.count} Termine`} />
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr),320px]">
        <div className="grid gap-4 min-w-0">
          <section className="rounded-xl border border-border bg-surface-card/80 p-3 shadow-card sm:p-4">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-center justify-between">
              <label className="relative block lg:max-w-sm lg:flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                <input ref={searchRef} className="input pl-9" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Suchen nach Titel, Ort, Kalender" />
              </label>
              <div className="flex items-center gap-2 overflow-x-auto pb-1 lg:pb-0">
                <span className="hidden shrink-0 items-center gap-1.5 rounded-md border border-border bg-bg/35 px-2.5 py-2 text-small text-ink-muted xl:inline-flex"><SlidersHorizontal size={14} />Filter</span>
                {calendars.map((cal) => {
                  const active = visibleCalendars.has(cal.id);
                  return (
                    <button
                      key={cal.id}
                      className={`shrink-0 inline-flex items-center gap-2 rounded-md border px-3 py-2 text-small ${active ? 'border-brand-primary/50 bg-brand-primary/15 text-ink-primary' : 'border-border bg-surface-elevated text-ink-muted'}`}
                      onClick={() => setVisibleCalendars((prev) => toggleSet(prev, cal.id))}
                    >
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: cal.color }} />
                      {cal.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          {mode === 'month' && <MonthGrid anchor={anchor} events={filteredEvents} onCreate={openCreate} onCreateRange={openCreateRange} onMove={(day) => { const event = draggedEvent(); if (event) moveEvent(event, day); }} onDragStart={setDraggingEventId} onSelect={setSelectedEventId} />}
          {mode === 'week' && <TimeGrid days={daysBetween(range.from, range.to)} events={filteredEvents} onCreate={openCreate} onCreateRange={openCreateTimedRange} onCreateAllDay={(day) => openCreateRange(day, day)} onMove={(target) => { const event = draggedEvent(); if (event) moveEvent(event, target, target.getHours(), target.getMinutes()); }} onResize={resizeEvent} onDragStart={setDraggingEventId} onSelect={setSelectedEventId} />}
          {mode === 'day' && <TimeGrid days={[startOfDay(anchor)]} events={filteredEvents} onCreate={openCreate} onCreateRange={openCreateTimedRange} onCreateAllDay={(day) => openCreateRange(day, day)} onMove={(target) => { const event = draggedEvent(); if (event) moveEvent(event, target, target.getHours(), target.getMinutes()); }} onResize={resizeEvent} onDragStart={setDraggingEventId} onSelect={setSelectedEventId} />}
          {mode === 'agenda' && <AgendaDays days={daysBetween(range.from, range.to)} events={filteredEvents} compact={false} onSelect={setSelectedEventId} />}
        </div>

        <aside className="grid gap-4 content-start">
          <DayFocusCard events={todayEvents} onCreate={() => openCreate(new Date())} onSelect={setSelectedEventId} />
          <section className="rounded-xl border border-border bg-surface-card p-5 shadow-card">
            <h2 className="text-h4 mb-3 inline-flex items-center gap-2"><Bell size={17} className="text-status-info" />Als naechstes</h2>
            <div className="grid gap-2">
              {upcoming.map((event) => <UpcomingItem key={event.occurrenceId ?? event.id} event={event} onSelect={setSelectedEventId} />)}
              {upcoming.length === 0 && <EmptyMini text="Keine kommenden Termine im Zeitraum." actionLabel="Termin planen" onAction={() => openCreate(new Date())} />}
            </div>
          </section>
          <CalendarPulse calendars={calendars} activeCalendars={activeCalendars} events={filteredEvents} />
        </aside>
      </div>

      <Modal open={quickOpen} onClose={() => setQuickOpen(false)} title="Termin anlegen" size="lg" footer={
        <>
          <button className="btn btn-ghost" onClick={() => setQuickOpen(false)}>Abbrechen</button>
          <button className="btn btn-primary" onClick={createEvent}>Speichern</button>
        </>
      }>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="label">Titel</label>
            <input className="input" value={quickForm.summary} onChange={(e) => setQuickForm({ ...quickForm, summary: e.target.value })} autoFocus />
          </div>
          <div>
            <label className="label">Kalender</label>
            <select className="select" value={quickForm.calendarId} onChange={(e) => setQuickForm({ ...quickForm, calendarId: e.target.value })}>
              {writableCalendars.map((cal) => <option key={cal.id} value={cal.id}>{cal.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Wiederholung</label>
            <select className="select" value={quickForm.recurrence} onChange={(e) => setQuickForm({ ...quickForm, recurrence: e.target.value as RecurrenceFrequency })}>
              <option value="NONE">Keine</option>
              <option value="DAILY">Taeglich</option>
              <option value="WEEKLY">Woechentlich</option>
              <option value="MONTHLY">Monatlich</option>
              <option value="YEARLY">Jaehrlich</option>
            </select>
          </div>
          {quickForm.recurrence !== 'NONE' && (
            <div className="sm:col-span-2 grid grid-cols-1 gap-3 rounded-lg border border-border bg-surface-elevated/70 p-3 sm:grid-cols-[minmax(0,1fr),minmax(0,1fr)]">
              <div>
                <label className="label">Intervall</label>
                <div className="flex items-center gap-2">
                  <span className="text-small text-ink-muted">alle</span>
                  <input
                    type="number"
                    min={1}
                    max={999}
                    className="input max-w-[110px]"
                    value={quickForm.recurrenceInterval}
                    onChange={(e) => setQuickForm({ ...quickForm, recurrenceInterval: e.target.value })}
                  />
                  <span className="text-small text-ink-muted">{recurrenceUnitLabel(quickForm.recurrence)}</span>
                </div>
              </div>
              <div>
                <label className="label">Endet</label>
                <select className="select" value={quickForm.recurrenceEndMode} onChange={(e) => setQuickForm({ ...quickForm, recurrenceEndMode: e.target.value as RecurrenceEndMode })}>
                  <option value="NEVER">Nie</option>
                  <option value="COUNT">Nach Wiederholungen</option>
                  <option value="UNTIL">An Datum</option>
                </select>
              </div>
              {quickForm.recurrence === 'WEEKLY' && (
                <div className="sm:col-span-2">
                  <label className="label">Wochentage</label>
                  <div className="flex flex-wrap gap-2">
                    {weekdayOptions.map((day) => {
                      const active = quickForm.recurrenceByDay.includes(day.value);
                      return (
                        <button
                          key={day.value}
                          type="button"
                          className={`h-9 min-w-10 rounded-md border px-3 text-small ${active ? 'border-brand-primary/55 bg-brand-primary/18 text-ink-primary' : 'border-border bg-bg/35 text-ink-muted hover:text-ink-primary'}`}
                          onClick={() => setQuickForm({ ...quickForm, recurrenceByDay: toggleArray(quickForm.recurrenceByDay, day.value) })}
                        >
                          {day.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {quickForm.recurrence === 'MONTHLY' && (
                <div className="sm:col-span-2 grid gap-3 rounded-md border border-border/70 bg-bg/25 p-3">
                  <div>
                    <label className="label">Monatliche Regel</label>
                    <div className="inline-flex rounded-md border border-border bg-bg/45 p-1">
                      {([
                        ['DATE', 'Datum'],
                        ['WEEKDAY', 'Wochentag'],
                      ] as const).map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          className={`rounded px-3 py-2 text-small ${quickForm.recurrenceMonthlyMode === value ? 'bg-white/12 text-ink-primary' : 'text-ink-muted hover:text-ink-primary'}`}
                          onClick={() => setQuickForm({ ...quickForm, recurrenceMonthlyMode: value })}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {quickForm.recurrenceMonthlyMode === 'DATE' ? (
                    <div>
                      <label className="label">Monatstag</label>
                      <input
                        type="number"
                        min={1}
                        max={31}
                        className="input max-w-[140px]"
                        value={quickForm.recurrenceMonthDay}
                        onChange={(e) => setQuickForm({ ...quickForm, recurrenceMonthDay: e.target.value })}
                      />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <label className="label">Position</label>
                        <select className="select" value={quickForm.recurrenceBySetPos} onChange={(e) => setQuickForm({ ...quickForm, recurrenceBySetPos: e.target.value })}>
                          {ordinalOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="label">Wochentag</label>
                        <select className="select" value={quickForm.recurrenceByWeekday} onChange={(e) => setQuickForm({ ...quickForm, recurrenceByWeekday: e.target.value })}>
                          {weekdayOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {quickForm.recurrence === 'YEARLY' && (
                <div className="sm:col-span-2 grid gap-3 rounded-md border border-border/70 bg-bg/25 p-3">
                  <div>
                    <label className="label">Jaehrliche Regel</label>
                    <div className="inline-flex rounded-md border border-border bg-bg/45 p-1">
                      {([
                        ['DATE', 'Datum'],
                        ['WEEKDAY', 'Wochentag im Monat'],
                      ] as const).map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          className={`rounded px-3 py-2 text-small ${quickForm.recurrenceYearlyMode === value ? 'bg-white/12 text-ink-primary' : 'text-ink-muted hover:text-ink-primary'}`}
                          onClick={() => setQuickForm({ ...quickForm, recurrenceYearlyMode: value })}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div>
                      <label className="label">Monat</label>
                      <select className="select" value={quickForm.recurrenceByMonth} onChange={(e) => setQuickForm({ ...quickForm, recurrenceByMonth: e.target.value })}>
                        {monthOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>
                    </div>
                    {quickForm.recurrenceYearlyMode === 'DATE' ? (
                      <div>
                        <label className="label">Tag</label>
                        <input
                          type="number"
                          min={1}
                          max={31}
                          className="input"
                          value={quickForm.recurrenceMonthDay}
                          onChange={(e) => setQuickForm({ ...quickForm, recurrenceMonthDay: e.target.value })}
                        />
                      </div>
                    ) : (
                      <>
                        <div>
                          <label className="label">Position</label>
                          <select className="select" value={quickForm.recurrenceBySetPos} onChange={(e) => setQuickForm({ ...quickForm, recurrenceBySetPos: e.target.value })}>
                            {ordinalOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="label">Wochentag</label>
                          <select className="select" value={quickForm.recurrenceByWeekday} onChange={(e) => setQuickForm({ ...quickForm, recurrenceByWeekday: e.target.value })}>
                            {weekdayOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
              {quickForm.recurrenceEndMode === 'COUNT' && (
                <div>
                  <label className="label">Anzahl</label>
                  <input
                    type="number"
                    min={1}
                    max={999}
                    className="input"
                    value={quickForm.recurrenceCount}
                    onChange={(e) => setQuickForm({ ...quickForm, recurrenceCount: e.target.value })}
                  />
                </div>
              )}
              {quickForm.recurrenceEndMode === 'UNTIL' && (
                <div>
                  <label className="label">Bis einschliesslich</label>
                  <input
                    type="date"
                    className="input"
                    value={quickForm.recurrenceUntil}
                    onChange={(e) => setQuickForm({ ...quickForm, recurrenceUntil: e.target.value })}
                  />
                </div>
              )}
            </div>
          )}
          <div>
            <label className="label">Start</label>
            <input type="datetime-local" className="input" value={quickForm.startsAt} onChange={(e) => setQuickForm({ ...quickForm, startsAt: e.target.value })} />
          </div>
          <div>
            <label className="label">Ende</label>
            <input type="datetime-local" className="input" value={quickForm.endsAt} onChange={(e) => setQuickForm({ ...quickForm, endsAt: e.target.value })} />
          </div>
          <label className="flex items-center gap-2 text-body">
            <input type="checkbox" checked={quickForm.allDay} onChange={(e) => setQuickForm({ ...quickForm, allDay: e.target.checked })} />
            Ganztags
          </label>
          <label className="flex items-center gap-2 text-body">
            <input type="checkbox" checked={quickForm.sendInvitations} onChange={(e) => setQuickForm({ ...quickForm, sendInvitations: e.target.checked })} />
            Einladungen senden
          </label>
          <div className="sm:col-span-2">
            <label className="label">Ort</label>
            <input className="input" value={quickForm.location} onChange={(e) => setQuickForm({ ...quickForm, location: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <label className="label">Teilnehmer</label>
            <textarea className="textarea font-mono" rows={3} value={attendeesInput} onChange={(e) => setAttendeesInput(e.target.value)} placeholder="kunde@example.com&#10;Anna Schmidt <anna@example.com>" />
          </div>
          <div className="sm:col-span-2">
            <label className="label">Erinnerungen</label>
            <ReminderPicker value={remindersInput} onChange={setRemindersInput} />
          </div>
        </div>
      </Modal>

      <EventInspector
        event={selectedEvent}
        loading={previewLoading}
        open={Boolean(selectedEventId)}
        onClose={() => setSelectedEventId(null)}
        onResend={resendFromPreview}
        onDuplicate={duplicateFromPreview}
        onCancel={cancelFromPreview}
      />
    </div>
  );
}

function MonthGrid({
  anchor,
  events,
  onCreate,
  onCreateRange,
  onMove,
  onDragStart,
  onSelect,
}: {
  anchor: Date;
  events: EventRow[];
  onCreate: (date: Date) => void;
  onCreateRange: (from: Date, toInclusive: Date) => void;
  onMove: (date: Date) => void;
  onDragStart: (id: string | null) => void;
  onSelect: (id: string) => void;
}) {
  const first = startOfWeek(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
  const cells = Array.from({ length: 42 }, (_, i) => addDays(first, i));
  const weeks = Array.from({ length: 6 }, (_, week) => cells.slice(week * 7, week * 7 + 7));
  const [selection, setSelection] = useState<{ start: Date; end: Date } | null>(null);
  const selectionRef = useRef<{ start: Date; end: Date } | null>(null);
  const selectingRef = useRef(false);
  const selectionMovedRef = useRef(false);

  const setSelectionState = (next: { start: Date; end: Date } | null) => {
    selectionRef.current = next;
    setSelection(next);
  };

  const dateFromPointer = (event: Pick<PointerEvent, 'clientX' | 'clientY'>) => {
    const el = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>('[data-month-day]');
    return el?.dataset.monthDay ? dateFromLocalInput(el.dataset.monthDay) : null;
  };

  const startSelection = (event: React.PointerEvent<HTMLDivElement>, day: Date) => {
    if (event.button !== 0 || (event.target as HTMLElement).closest('[data-event-pill="true"]')) return;
    event.preventDefault();
    selectingRef.current = true;
    selectionMovedRef.current = false;
    setSelectionState({ start: day, end: day });
  };

  const extendSelection = (day: Date) => {
    if (!selectingRef.current) return;
    const current = selectionRef.current;
    if (!current) return;
    if (!isSameDay(current.start, day)) selectionMovedRef.current = true;
    setSelectionState({ ...current, end: day });
  };

  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      if (!selectingRef.current) return;
      const day = dateFromPointer(event);
      if (day) extendSelection(day);
    };
    const onPointerUp = (event: PointerEvent) => {
      if (!selectingRef.current) return;
      selectingRef.current = false;
      const pointerDay = dateFromPointer(event);
      const current = selectionRef.current;
      setSelectionState(null);
      if (!current) return;
      const end = pointerDay ?? current.end;
      if (selectionMovedRef.current || !isSameDay(current.start, end)) {
        onCreateRange(current.start, end);
        return;
      }
      onCreate(current.start);
    };
    const onPointerCancel = () => {
      if (!selectingRef.current) return;
      selectingRef.current = false;
      setSelectionState(null);
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onCreate, onCreateRange]);

  const extendSelectionFromCell = (day: Date) => {
    if (!selectingRef.current) return;
    setSelection((current) => {
      if (!current) return current;
      if (!isSameDay(current.start, day)) selectionMovedRef.current = true;
      const next = { ...current, end: day };
      selectionRef.current = next;
      return next;
    });
  };

  return (
    <>
      <div className="hidden overflow-hidden rounded-xl border border-border bg-surface-card shadow-card md:block">
        <div className="grid grid-cols-7 bg-[linear-gradient(180deg,rgba(255,255,255,0.055),rgba(255,255,255,0.018))] text-meta uppercase tracking-[0.16em] text-ink-muted">
          {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((d) => <div key={d} className="px-3 py-3">{d}</div>)}
        </div>
        <div>
          {weeks.map((weekDays) => {
            const weekStart = weekDays[0];
            const bars = layoutMonthRangeBars(events, weekStart);
            const visibleBars = bars.filter((bar) => bar.lane < 4);
            const hiddenBars = bars.length - visibleBars.length;
            const laneCount = Math.min(4, Math.max(visibleBars.length ? Math.max(...visibleBars.map((bar) => bar.lane)) + 1 : 0, hiddenBars ? 1 : 0));
            const rowMinHeight = Math.max(144, 116 + laneCount * 28);
            return (
              <div key={weekStart.toISOString()} className="relative">
                <div className="grid grid-cols-7">
                  {weekDays.map((day) => {
                    const daily = eventsForDay(events, day);
                    const singleDayEvents = daily.filter((event) => !spansMultipleDays(event));
                    const muted = day.getMonth() !== anchor.getMonth();
                    const today = isSameDay(day, new Date());
                    const weekend = day.getDay() === 0 || day.getDay() === 6;
                    const selected = Boolean(selection && isDayInSelection(day, selection.start, selection.end));
                    const rangeEdge = Boolean(selection && (isSameDay(day, selection.start) || isSameDay(day, selection.end)));
                    const visibleSingleDayLimit = Math.max(1, 4 - laneCount);
                    const visibleRangeCount = visibleBars.filter((bar) => dayIndexInBar(day, weekStart, bar)).length;
                    const hiddenDailyCount = daily.length - visibleRangeCount - Math.min(singleDayEvents.length, visibleSingleDayLimit);
                    return (
                      <div
                        key={day.toISOString()}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter') onCreate(day); }}
                        onPointerDown={(e) => startSelection(e, day)}
                        onPointerEnter={() => extendSelectionFromCell(day)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => { e.preventDefault(); onMove(day); }}
                        data-month-day={toLocalDateInput(day)}
                        className={`group relative text-left border-r border-b border-border/70 p-2.5 transition-colors hover:bg-white/[0.045] focus:outline-none focus:ring-2 focus:ring-brand-primary/40 ${muted ? 'bg-black/10 text-ink-muted' : weekend ? 'bg-white/[0.02]' : 'bg-transparent'} ${selected ? 'bg-brand-primary/12 ring-1 ring-inset ring-brand-primary/35' : ''} ${rangeEdge ? 'shadow-[inset_0_0_0_1px_rgba(20,184,166,0.55)]' : ''}`}
                        style={{ minHeight: rowMinHeight }}
                      >
                        {today && <div className="pointer-events-none absolute inset-x-2 top-0 h-px bg-gradient-to-r from-brand-pink/20 via-brand-primary to-status-info/20" />}
                        <div className="mb-2 flex items-center justify-between">
                          <div className={`inline-flex h-7 w-7 items-center justify-center rounded-md text-small ${today ? 'bg-brand-primary text-white shadow-primary' : muted ? 'text-ink-muted' : 'text-ink-secondary group-hover:bg-white/7'}`}>{day.getDate()}</div>
                          {daily.length > 0 ? <span className="rounded-full bg-white/[0.07] px-2 py-0.5 text-[11px] text-ink-secondary">{daily.length}</span> : <span className="h-1.5 w-1.5 rounded-full bg-white/[0.06] opacity-0 transition-opacity group-hover:opacity-100" />}
                        </div>
                        <div style={{ height: laneCount * 28 }} />
                        <div className="grid gap-1.5">
                          {singleDayEvents.slice(0, visibleSingleDayLimit).map((event) => <EventPill key={event.occurrenceId ?? event.id} event={event} onDragStart={onDragStart} onSelect={onSelect} />)}
                          {hiddenDailyCount > 0 && <div className="text-[11px] text-ink-muted">+{hiddenDailyCount} weitere</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="pointer-events-none absolute inset-x-2 top-[44px] h-0">
                  {visibleBars.map((bar) => (
                    <MonthRangeBar
                      key={`${bar.event.occurrenceId ?? bar.event.id}-${bar.lane}-${bar.startIndex}`}
                      bar={bar}
                      onDragStart={onDragStart}
                      onSelect={onSelect}
                    />
                  ))}
                  {hiddenBars > 0 && (
                    <div className="absolute right-2 rounded-full border border-border bg-surface-elevated px-2 py-0.5 text-[11px] text-ink-muted" style={{ top: laneCount * 28 - 2 }}>
                      +{hiddenBars} Mehrtages-Termine
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="md:hidden">
        <AgendaDays days={cells.filter((d) => d.getMonth() === anchor.getMonth())} events={events} compact={false} onSelect={onSelect} />
      </div>
    </>
  );
}

function TimeGrid({
  days,
  events,
  onCreate,
  onCreateRange,
  onCreateAllDay,
  onMove,
  onResize,
  onDragStart,
  onSelect,
}: {
  days: Date[];
  events: EventRow[];
  onCreate: (date: Date, hour: number) => void;
  onCreateRange: (startsAt: Date, endsAt: Date) => void;
  onCreateAllDay: (date: Date) => void;
  onMove: (date: Date) => void;
  onResize: (event: EventRow, startsAt: Date, endsAt: Date) => void;
  onDragStart: (id: string | null) => void;
  onSelect: (id: string) => void;
}) {
  const startHour = 6;
  const endHour = 22;
  const slotMinutes = 30;
  const slotHeight = 34;
  const slots = Array.from({ length: (endHour - startHour) * (60 / slotMinutes) }, (_, i) => i);
  const hours = Array.from({ length: endHour - startHour }, (_, i) => i + startHour);
  const totalHeight = slots.length * slotHeight;
  const [selection, setSelection] = useState<{ start: Date; end: Date } | null>(null);
  const selectionRef = useRef<{ start: Date; end: Date } | null>(null);
  const selectingRef = useRef(false);
  const selectionMovedRef = useRef(false);
  const [resizePreview, setResizePreview] = useState<{ eventId: string; startsAt: Date; endsAt: Date } | null>(null);
  const resizePreviewRef = useRef<{ eventId: string; startsAt: Date; endsAt: Date } | null>(null);
  const resizeRef = useRef<{ event: EventRow; edge: 'start' | 'end'; startsAt: Date; endsAt: Date } | null>(null);
  const now = new Date();

  const setSelectionState = (next: { start: Date; end: Date } | null) => {
    selectionRef.current = next;
    setSelection(next);
  };

  const slotStart = (day: Date, slot: number) => {
    const out = startOfDay(day);
    out.setHours(startHour, slot * slotMinutes, 0, 0);
    return out;
  };

  const slotFromPointer = (event: Pick<PointerEvent, 'clientX' | 'clientY'>) => {
    const el = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>('[data-time-slot],[data-time-day]');
    if (!el) return null;
    if (el.dataset.timeSlot) return dateFromLocalDateTimeInput(el.dataset.timeSlot);
    if (!el.dataset.timeDay) return null;
    const rect = el.getBoundingClientRect();
    const slot = Math.max(0, Math.min(slots.length - 1, Math.floor((event.clientY - rect.top) / slotHeight)));
    return slotStart(dateFromLocalInput(el.dataset.timeDay), slot);
  };

  const selectedRange = selection ? orderedDateRange(selection.start, selection.end) : null;

  const beginSelection = (event: React.PointerEvent<HTMLDivElement>, start: Date) => {
    if (event.button !== 0 || (event.target as HTMLElement).closest('[data-event-pill="true"]')) return;
    event.preventDefault();
    selectingRef.current = true;
    selectionMovedRef.current = false;
    setSelectionState({ start, end: addMinutes(start, slotMinutes) });
  };

  const extendSelection = (slot: Date) => {
    if (!selectingRef.current) return;
    const current = selectionRef.current;
    if (!current) return;
    const nextEnd = slot.getTime() < current.start.getTime() ? slot : addMinutes(slot, slotMinutes);
    if (Math.abs(nextEnd.getTime() - current.end.getTime()) >= slotMinutes * 60000) selectionMovedRef.current = true;
    setSelectionState({ start: current.start, end: nextEnd });
  };

  const beginResize = (event: EventRow, edge: 'start' | 'end') => {
    const nextPreview = { eventId: event.id, startsAt: new Date(event.startsAt), endsAt: new Date(event.endsAt) };
    resizeRef.current = {
      event,
      edge,
      startsAt: nextPreview.startsAt,
      endsAt: nextPreview.endsAt,
    };
    resizePreviewRef.current = nextPreview;
    setResizePreview(nextPreview);
  };

  const extendResize = (slot: Date) => {
    const current = resizeRef.current;
    if (!current) return;
    if (current.edge === 'start') {
      const latestStart = addMinutes(current.endsAt, -slotMinutes);
      const startsAt = slot.getTime() < latestStart.getTime() ? slot : latestStart;
      const nextPreview = { eventId: current.event.id, startsAt, endsAt: current.endsAt };
      resizePreviewRef.current = nextPreview;
      setResizePreview(nextPreview);
      return;
    }
    const earliestEnd = addMinutes(current.startsAt, slotMinutes);
    const endsAt = addMinutes(slot, slotMinutes).getTime() > earliestEnd.getTime() ? addMinutes(slot, slotMinutes) : earliestEnd;
    const nextPreview = { eventId: current.event.id, startsAt: current.startsAt, endsAt };
    resizePreviewRef.current = nextPreview;
    setResizePreview(nextPreview);
  };

  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      if (resizeRef.current) {
        const slot = slotFromPointer(event);
        if (slot) extendResize(slot);
        return;
      }
      if (!selectingRef.current) return;
      const slot = slotFromPointer(event);
      if (slot) extendSelection(slot);
    };
    const onPointerUp = (event: PointerEvent) => {
      if (resizeRef.current) {
        const slot = slotFromPointer(event);
        if (slot) extendResize(slot);
        const current = resizeRef.current;
        const preview = resizePreviewRef.current;
        resizeRef.current = null;
        resizePreviewRef.current = null;
        setResizePreview(null);
        if (preview) onResize(current.event, preview.startsAt, preview.endsAt);
        return;
      }
      if (!selectingRef.current) return;
      selectingRef.current = false;
      const pointerSlot = slotFromPointer(event);
      if (pointerSlot) extendSelection(pointerSlot);
      const current = selectionRef.current;
      setSelectionState(null);
      if (!current) return;
      const range = orderedDateRange(current.start, current.end);
      const isSingleSlot = range.end.getTime() - range.start.getTime() <= slotMinutes * 60000;
      onCreateRange(range.start, isSingleSlot && !selectionMovedRef.current ? addMinutes(range.start, 60) : range.end);
    };
    const onPointerCancel = () => {
      resizeRef.current = null;
      resizePreviewRef.current = null;
      setResizePreview(null);
      if (!selectingRef.current) return;
      selectingRef.current = false;
      setSelectionState(null);
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onCreateRange, onResize]);

  return (
    <div className="overflow-auto rounded-xl border border-border bg-surface-card shadow-card">
      <div className="min-w-[760px]">
        <div className="grid border-b border-border bg-bg/45" style={{ gridTemplateColumns: `72px repeat(${days.length}, minmax(140px, 1fr))` }}>
          <div className="border-r border-border/60" />
          {days.map((day) => (
            <div key={day.toISOString()} className={`px-3 py-3 ${isSameDay(day, now) ? 'bg-brand-primary/10' : ''}`}>
              <div className="text-small font-semibold text-ink-secondary">{day.toLocaleDateString('de-DE', { weekday: 'short' })}</div>
              <div className={`text-h4 ${isSameDay(day, now) ? 'text-brand-primary' : ''}`}>{day.getDate()}</div>
            </div>
          ))}
        </div>
        <div className="grid border-b border-border/60 bg-bg/25" style={{ gridTemplateColumns: `72px repeat(${days.length}, minmax(140px, 1fr))` }}>
          <div className="border-r border-border/60 px-3 py-3 text-small text-ink-muted">ganztags</div>
          {days.map((day) => {
            const allDayEvents = eventsForDay(events, day).filter(isAllDayLaneEvent);
            return (
              <div
                key={`all-day-${day.toISOString()}`}
                role="button"
                tabIndex={0}
                onClick={() => onCreateAllDay(day)}
                onKeyDown={(e) => { if (e.key === 'Enter') onCreateAllDay(day); }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); onMove(startOfDay(day)); }}
                className={`min-h-[58px] border-l border-border/60 p-2 transition-colors hover:bg-white/[0.04] ${isSameDay(day, now) ? 'bg-brand-primary/[0.035]' : ''}`}
              >
                <div className="grid gap-1">
                  {allDayEvents.slice(0, 3).map((event) => <EventPill key={event.occurrenceId ?? event.id} event={event} onDragStart={onDragStart} onSelect={onSelect} />)}
                  {allDayEvents.length > 3 && <div className="text-[11px] text-ink-muted">+{allDayEvents.length - 3} weitere</div>}
                </div>
              </div>
            );
          })}
        </div>
        <div className="grid" style={{ gridTemplateColumns: `72px repeat(${days.length}, minmax(140px, 1fr))` }}>
          <div className="relative border-r border-border/60" style={{ height: totalHeight }}>
            {hours.map((hour) => (
              <div key={hour} className="absolute left-0 right-0 border-t border-border/50 px-3 pt-1 font-mono text-small text-ink-muted" style={{ top: (hour - startHour) * (60 / slotMinutes) * slotHeight }}>
                {String(hour).padStart(2, '0')}:00
              </div>
            ))}
          </div>
          {days.map((day) => {
            const timedEvents = eventsForDay(events, day).filter((event) => !isAllDayLaneEvent(event));
            const laidOutEvents = layoutTimedEvents(timedEvents);
            const showNow = isSameDay(day, now) && now.getHours() >= startHour && now.getHours() < endHour;
            const nowTop = ((now.getHours() - startHour) * 60 + now.getMinutes()) / slotMinutes * slotHeight;
            return (
              <div key={`time-${day.toISOString()}`} data-time-day={toLocalDateInput(day)} className={`relative border-l border-border/60 ${isSameDay(day, now) ? 'bg-brand-primary/[0.025]' : ''}`} style={{ height: totalHeight }}>
                {slots.map((slot) => {
                  const start = slotStart(day, slot);
                  const end = addMinutes(start, slotMinutes);
                  const selected = Boolean(selectedRange && start < selectedRange.end && end > selectedRange.start);
                  return (
                    <div
                      key={`${day.toISOString()}-${slot}`}
                      role="button"
                      tabIndex={0}
                      data-time-slot={toLocalInput(start)}
                      onPointerDown={(e) => beginSelection(e, start)}
                      onPointerEnter={() => extendSelection(start)}
                      onKeyDown={(e) => { if (e.key === 'Enter') onCreate(day, start.getHours()); }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => { e.preventDefault(); onMove(start); }}
                      className={`absolute left-0 right-0 border-t border-border/45 px-2 transition-colors hover:bg-white/[0.035] focus:outline-none focus:ring-2 focus:ring-brand-primary/30 ${slot % 2 === 1 ? 'border-dashed' : ''} ${selected ? 'bg-brand-primary/16 ring-1 ring-inset ring-brand-primary/35' : ''}`}
                      style={{ top: slot * slotHeight, height: slotHeight }}
                    />
                  );
                })}
                {showNow && <div className="pointer-events-none absolute left-0 right-0 z-20 h-px bg-brand-pink shadow-[0_0_12px_rgba(236,72,153,0.65)]" style={{ top: nowTop }} />}
                {laidOutEvents.map(({ event, column, columns }) => {
                  const preview = resizePreview?.eventId === event.id ? resizePreview : null;
                  const renderEvent = preview ? { ...event, startsAt: preview.startsAt.toISOString(), endsAt: preview.endsAt.toISOString() } : event;
                  const position = timeEventPosition(renderEvent, startHour, endHour, slotHeight, slotMinutes);
                  if (!position) return null;
                  return (
                    <EventBlock
                      key={event.occurrenceId ?? event.id}
                      event={renderEvent}
                      onDragStart={onDragStart}
                      onResizeStart={(edge) => beginResize(event, edge)}
                      onSelect={onSelect}
                      style={{
                        top: position.top,
                        height: position.height,
                        left: `calc(${(column / columns) * 100}% + 6px)`,
                        width: `calc(${100 / columns}% - 10px)`,
                      }}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function AgendaDays({ days, events, compact, onSelect }: { days: Date[]; events: EventRow[]; compact: boolean; onSelect?: (id: string) => void }) {
  return (
    <div className={compact ? 'grid gap-2' : 'grid gap-3'}>
      {days.map((day) => {
        const daily = eventsForDay(events, day);
        if (compact && daily.length === 0) return <div key={day.toISOString()} className="text-small text-ink-muted">Heute keine Termine.</div>;
        if (!compact && daily.length === 0) return null;
        return (
          <section key={day.toISOString()} className={compact ? '' : 'card'}>
            {!compact && (
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-h4">{day.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' })}</h2>
                <span className="badge badge-muted">{daily.length} Termine</span>
              </div>
            )}
            <div className="grid gap-2">
              {daily.map((event) => (
                <button key={event.occurrenceId ?? event.id} type="button" onClick={() => onSelect?.(event.id)} className={`bg-surface-elevated border border-border rounded-md px-3 py-2 grid gap-2 text-left hover:bg-white/[0.04] ${compact ? '' : 'sm:grid-cols-[120px,1fr,160px] sm:items-center'}`}>
                  <span className="font-mono text-small text-ink-secondary">{timeRange(event)}</span>
                  <span className="min-w-0">
                    <span className="font-medium break-words">{event.summary}</span>
                    {event.location && <span className="text-small text-ink-muted sm:ml-2 block sm:inline">{event.location}</span>}
                  </span>
                  {!compact && (
                    <span className="inline-flex items-center gap-2 text-small text-ink-secondary">
                      <span className="w-2 h-2 rounded-full" style={{ background: event.calendar.color }} />
                      {event.calendar.name}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </section>
        );
      })}
      {!compact && days.every((day) => eventsForDay(events, day).length === 0) && <EmptyState />}
    </div>
  );
}

function EventPill({ event, onDragStart, onSelect }: { event: EventRow; onDragStart: (id: string | null) => void; onSelect: (id: string) => void }) {
  return (
    <button
      type="button"
      data-event-pill="true"
      draggable
      onDragStart={(e) => { e.dataTransfer.setData('text/plain', event.id); onDragStart(event.id); }}
      onDragEnd={() => onDragStart(null)}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); onSelect(event.id); }}
      className="group/event block w-full rounded-md border px-2 py-1.5 text-left text-[11px] leading-tight shadow-[inset_0_0_0_1px_rgba(255,255,255,0.04)] cursor-grab active:cursor-grabbing hover:brightness-110"
      style={{ borderColor: `${event.calendar.color}99`, background: eventBackground(event.calendar.color) }}
    >
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="font-mono text-ink-primary/70">{event.allDay ? 'Tag' : new Date(event.startsAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
        {event.rrule && <Repeat2 size={11} className="shrink-0 text-ink-primary/55" />}
        <span className="truncate font-medium text-ink-primary">{event.summary}</span>
      </span>
    </button>
  );
}

interface MonthRangeBarLayout {
  event: EventRow;
  startIndex: number;
  span: number;
  lane: number;
  continuesBefore: boolean;
  continuesAfter: boolean;
}

function MonthRangeBar({
  bar,
  onDragStart,
  onSelect,
}: {
  bar: MonthRangeBarLayout;
  onDragStart: (id: string | null) => void;
  onSelect: (id: string) => void;
}) {
  const { event } = bar;
  return (
    <button
      type="button"
      data-event-pill="true"
      draggable
      onDragStart={(e) => { e.dataTransfer.setData('text/plain', event.id); onDragStart(event.id); }}
      onDragEnd={() => onDragStart(null)}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); onSelect(event.id); }}
      className={`pointer-events-auto absolute z-10 min-w-0 border px-2 py-1 text-left text-[11px] leading-tight shadow-[0_8px_18px_rgba(0,0,0,0.18),inset_0_0_0_1px_rgba(255,255,255,0.045)] cursor-grab active:cursor-grabbing hover:z-20 hover:brightness-110 ${bar.continuesBefore ? 'rounded-l-sm border-l-white/20' : 'rounded-l-md'} ${bar.continuesAfter ? 'rounded-r-sm border-r-white/20' : 'rounded-r-md'}`}
      style={{
        left: `calc(${(bar.startIndex / 7) * 100}% + ${bar.continuesBefore ? 0 : 4}px)`,
        width: `calc(${(bar.span / 7) * 100}% - ${bar.continuesBefore ? 0 : 4}px - ${bar.continuesAfter ? 0 : 4}px)`,
        top: bar.lane * 28,
        borderColor: `${event.calendar.color}aa`,
        background: eventBackground(event.calendar.color),
      }}
    >
      <span className="flex min-w-0 items-center gap-1.5">
        {bar.continuesBefore && <span className="font-mono text-ink-primary/55">&lt;</span>}
        <span className="font-mono text-ink-primary/70">{event.allDay ? 'Tag' : timeRange(event)}</span>
        {event.rrule && <Repeat2 size={11} className="shrink-0 text-ink-primary/55" />}
        <span className="truncate font-medium text-ink-primary">{event.summary}</span>
        {bar.continuesAfter && <span className="ml-auto font-mono text-ink-primary/55">&gt;</span>}
      </span>
    </button>
  );
}

function EventBlock({
  event,
  onDragStart,
  onResizeStart,
  onSelect,
  style,
}: {
  event: EventRow;
  onDragStart: (id: string | null) => void;
  onResizeStart?: (edge: 'start' | 'end') => void;
  onSelect: (id: string) => void;
  style?: React.CSSProperties;
}) {
  return (
    <button
      type="button"
      data-event-pill="true"
      draggable
      onDragStart={(e) => { e.dataTransfer.setData('text/plain', event.id); onDragStart(event.id); }}
      onDragEnd={() => onDragStart(null)}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); onSelect(event.id); }}
      className={`${style ? 'absolute z-10 overflow-hidden' : 'block'} group/event relative rounded-md border px-2.5 py-2 min-w-0 cursor-grab active:cursor-grabbing text-left hover:z-20 hover:brightness-110`}
      style={{ borderColor: `${event.calendar.color}99`, background: eventBackground(event.calendar.color), ...style }}
    >
      <div className="flex items-center gap-1.5 font-medium text-small text-ink-primary">
        <GripVertical size={12} className="text-ink-primary/45" />
        <span className="truncate">{event.summary}</span>
        {event.rrule && <Repeat2 size={12} className="shrink-0 text-ink-primary/55" />}
      </div>
      <div className="mt-1 font-mono text-[11px] text-ink-primary/65">{timeRange(event)}</div>
      {event.location && <div className="mt-1 flex items-center gap-1 text-[11px] text-ink-primary/55"><MapPin size={11} />{event.location}</div>}
      {onResizeStart && (
        <>
          <span
            aria-hidden="true"
            draggable={false}
            className="absolute inset-x-2 top-0 z-20 h-3 cursor-ns-resize rounded-b bg-white/0 transition-colors group-hover/event:bg-white/25"
            onDragStart={(e) => e.preventDefault()}
            onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); onResizeStart('start'); }}
          />
          <span
            aria-hidden="true"
            draggable={false}
            className="absolute inset-x-2 bottom-0 z-20 h-3 cursor-ns-resize rounded-t bg-white/0 transition-colors group-hover/event:bg-white/25"
            onDragStart={(e) => e.preventDefault()}
            onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); onResizeStart('end'); }}
          />
        </>
      )}
    </button>
  );
}

function UpcomingItem({ event, onSelect }: { event: EventRow; onSelect: (id: string) => void }) {
  return (
    <button type="button" onClick={() => onSelect(event.id)} className="grid gap-1 rounded-md border px-3 py-2.5 text-left hover:brightness-110" style={{ borderColor: `${event.calendar.color}66`, background: eventBackground(event.calendar.color) }}>
      <div className="flex items-center gap-2 min-w-0">
        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: event.calendar.color }} />
        <span className="font-medium truncate text-ink-primary">{event.summary}</span>
      </div>
      <div className="text-small text-ink-primary/65">{new Date(event.startsAt).toLocaleString('de-DE')} · {event.calendar.name}</div>
    </button>
  );
}

function DayFocusCard({ events, onCreate, onSelect }: { events: EventRow[]; onCreate: () => void; onSelect: (id: string) => void }) {
  const now = new Date();
  return (
    <section className="overflow-hidden rounded-xl border border-border bg-[linear-gradient(180deg,rgba(20,184,166,0.11),rgba(255,255,255,0.025))] shadow-card">
      <div className="border-b border-border/70 p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-meta uppercase tracking-[0.16em] text-ink-muted">{now.toLocaleDateString('de-DE', { weekday: 'long' })}</div>
            <div className="mt-1 text-[34px] font-bold leading-none text-ink-primary">{now.getDate()}</div>
            <div className="mt-1 text-small text-ink-secondary">{now.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</div>
          </div>
          <button className="btn btn-primary !px-3" onClick={onCreate} title="Termin anlegen"><Plus size={17} /></button>
        </div>
      </div>
      <div className="grid gap-2 p-5">
        {events.length > 0 ? (
          events.map((event) => <UpcomingItem key={event.occurrenceId ?? event.id} event={event} onSelect={onSelect} />)
        ) : (
          <EmptyMini text="Heute ist frei." actionLabel="Termin planen" onAction={onCreate} />
        )}
      </div>
    </section>
  );
}

function EventInspector({
  event,
  loading,
  open,
  onClose,
  onResend,
  onDuplicate,
  onCancel,
}: {
  event: EventPreview | null;
  loading: boolean;
  open: boolean;
  onClose: () => void;
  onResend: (method: 'REQUEST' | 'REMINDER' | 'CANCEL') => void;
  onDuplicate: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <button className="absolute inset-0 bg-black/55" onClick={onClose} aria-label="Termin-Inspector schliessen" />
      <aside className="absolute inset-y-0 right-0 flex w-full max-w-[460px] flex-col border-l border-border bg-bg shadow-card">
        <div className="flex items-start justify-between gap-4 border-b border-border p-5">
          <div className="min-w-0">
            <div className="text-meta uppercase tracking-[0.16em] text-ink-muted">Termin-Inspector</div>
            <h2 className="mt-1 truncate text-h3">{loading ? 'Lade Termin' : event?.summary ?? 'Termin'}</h2>
          </div>
          <button className="btn btn-ghost !px-2" onClick={onClose} title="Schliessen"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading && <div className="text-small text-ink-muted">Termin wird geladen …</div>}
          {!loading && event && (
            <div className="grid gap-4">
              <section className="rounded-lg border border-border bg-surface-card p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <span className="inline-flex items-center gap-2 text-small text-ink-secondary">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: event.calendar.color }} />
                    {event.calendar.name}
                  </span>
                  <Badge tone={event.status === 'CANCELLED' ? 'danger' : event.status === 'TENTATIVE' ? 'warning' : 'info'}>{event.status.toLowerCase()}</Badge>
                </div>
                <div className="grid gap-2 text-body text-ink-secondary">
                  <div className="font-mono text-small text-ink-primary">{timeRange(event)}</div>
                  <div>{new Date(event.startsAt).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</div>
                  {event.location && <div className="inline-flex items-center gap-2"><MapPin size={15} />{event.location}</div>}
                  {event.rrule && <div className="inline-flex items-center gap-2"><Repeat2 size={15} />{event.rrule}</div>}
                </div>
                {event.description && <p className="mt-3 whitespace-pre-wrap border-t border-border pt-3 text-body text-ink-secondary">{event.description}</p>}
              </section>

              <section className="rounded-lg border border-border bg-surface-card p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-h4">Teilnehmer</h3>
                  <span className="badge badge-muted">{event.attendees.length}</span>
                </div>
                <div className="grid gap-2">
                  {event.attendees.map((attendee) => (
                    <div key={attendee.id} className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface-elevated px-3 py-2">
                      <div className="min-w-0">
                        <div className="truncate text-body text-ink-primary">{attendee.name ?? attendee.email}</div>
                        <div className="truncate text-small text-ink-muted">{attendee.email}</div>
                      </div>
                      <Badge tone={rsvpTone(attendee.rsvp)}>{rsvpLabel(attendee.rsvp)}</Badge>
                    </div>
                  ))}
                  {event.attendees.length === 0 && <EmptyMini text="Keine Teilnehmer." />}
                </div>
              </section>

              <section className="rounded-lg border border-border bg-surface-card p-4">
                <h3 className="mb-3 text-h4">Erinnerungen</h3>
                <div className="flex flex-wrap gap-2">
                  {event.reminders.map((reminder) => (
                    <span key={reminder.id} className={`badge ${reminder.sentAt ? 'badge-success' : 'badge-muted'}`}>
                      {formatReminder(reminder.minutesBefore)} · {reminder.channel === 'EMAIL' ? 'Mail' : 'Push'}
                    </span>
                  ))}
                  {event.reminders.length === 0 && <span className="text-small text-ink-muted">Keine Erinnerungen.</span>}
                </div>
              </section>

              <section className="rounded-lg border border-border bg-surface-card p-4">
                <h3 className="mb-3 text-h4">Versand</h3>
                <div className="grid gap-2">
                  {event.deliveries.slice(0, 4).map((delivery) => (
                    <div key={delivery.id} className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface-elevated px-3 py-2">
                      <div className="min-w-0">
                        <div className="truncate text-small text-ink-secondary">{delivery.toEmail}</div>
                        <div className="font-mono text-[11px] text-ink-muted">{delivery.method} · {new Date(delivery.sentAt ?? delivery.failedAt ?? delivery.queuedAt).toLocaleString('de-DE')}</div>
                      </div>
                      <Badge tone={deliveryTone(delivery.status)}>{delivery.status.toLowerCase()}</Badge>
                    </div>
                  ))}
                  {event.deliveries.length === 0 && <EmptyMini text="Noch kein Mailversand." />}
                </div>
              </section>
            </div>
          )}
        </div>

        {event && (
          <div className="border-t border-border p-4">
            <div className="mb-3 grid grid-cols-2 gap-2">
              <button className="btn btn-secondary !px-2" onClick={() => onResend('REQUEST')} disabled={event.attendees.length === 0}>Einladung</button>
              <button className="btn btn-secondary !px-2" onClick={() => onResend('REMINDER')} disabled={event.attendees.length === 0}>Reminder</button>
              <button className="btn btn-secondary !px-2" onClick={onDuplicate}>Duplizieren</button>
              <button className="btn btn-danger !px-2" onClick={onCancel} disabled={event.status === 'CANCELLED'}>Absagen</button>
            </div>
            <Link to={`/events/${event.id}`} className="btn btn-primary w-full" onClick={onClose}>
              Vollansicht <ExternalLink size={16} />
            </Link>
          </div>
        )}
      </aside>
    </div>
  );
}

function CalendarPulse({ calendars, activeCalendars, events }: { calendars: Calendar[]; activeCalendars: number; events: EventRow[] }) {
  const invited = events.filter((event) => event.status !== 'CANCELLED').length;
  return (
    <section className="rounded-xl border border-border bg-surface-card p-5 shadow-card">
      <h2 className="mb-3 inline-flex items-center gap-2 text-h4"><CheckCircle2 size={17} className="text-status-success" />Status</h2>
      <div className="grid gap-2">
        <div className="flex items-center justify-between rounded-md border border-border bg-surface-elevated px-3 py-2.5">
          <span className="inline-flex items-center gap-2 text-small text-ink-secondary"><CalendarDays size={15} />Kalender aktiv</span>
          <span className="font-mono text-small text-ink-primary">{activeCalendars}/{calendars.length || 0}</span>
        </div>
        <div className="flex items-center justify-between rounded-md border border-border bg-surface-elevated px-3 py-2.5">
          <span className="inline-flex items-center gap-2 text-small text-ink-secondary"><Users size={15} />Sichtbare Termine</span>
          <span className="font-mono text-small text-ink-primary">{invited}</span>
        </div>
      </div>
    </section>
  );
}

function EmptyMini({ text, actionLabel, onAction }: { text: string; actionLabel?: string; onAction?: () => void }) {
  return (
    <div className="grid gap-3 rounded-md border border-dashed border-border bg-white/[0.025] px-3 py-4 text-small text-ink-muted">
      <span>{text}</span>
      {actionLabel && onAction && (
        <button className="btn btn-secondary !min-h-0 !justify-start !py-1.5" onClick={onAction}>
          <Plus size={14} />{actionLabel}
        </button>
      )}
    </div>
  );
}

function HeroMetric({ icon: Icon, label, value, detail }: { icon: LucideIcon; label: string; value: string; detail: string }) {
  return (
    <div className="rounded-lg border border-border/80 bg-surface-elevated/65 px-3 py-2.5">
      <div className="mb-1 flex items-center gap-2 text-meta uppercase tracking-[0.14em] text-ink-muted">
        <Icon size={14} className="text-ink-secondary" />
        {label}
      </div>
      <div className="flex items-baseline gap-2 min-w-0">
        <span className="truncate text-h4 text-ink-primary">{value}</span>
        <span className="truncate text-small text-ink-muted">{detail}</span>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-xl border border-dashed border-border bg-surface-card/70 p-8 text-center">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-md border border-border bg-surface-elevated text-ink-secondary">
        <CalendarDays size={20} />
      </div>
      <div className="text-h4">Keine Termine im Zeitraum</div>
      <div className="mt-1 text-small text-ink-muted">Der Zeitraum ist frei.</div>
    </div>
  );
}

function ReminderPicker({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const selected = new Set(value.split(',').filter(Boolean));
  const toggle = (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(Array.from(next).join(','));
  };
  const options = [
    ['0:NOTIFICATION', 'Zum Start'],
    ['5:NOTIFICATION', '5 Min.'],
    ['15:NOTIFICATION', '15 Min.'],
    ['60:NOTIFICATION', '1 Std.'],
    ['1440:NOTIFICATION', '1 Tag'],
    ['15:EMAIL', '15 Min. Mail'],
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(([key, label]) => (
        <button key={key} type="button" className={`rounded-md border px-3 py-2 text-small ${selected.has(key) ? 'border-brand-primary/50 bg-brand-primary/15 text-ink-primary' : 'border-border bg-surface-elevated text-ink-muted'}`} onClick={() => toggle(key)}>
          {label}
        </button>
      ))}
    </div>
  );
}

function visibleRange(anchor: Date, mode: ViewMode) {
  if (mode === 'month') {
    const from = startOfWeek(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
    return { from, to: addDays(from, 42) };
  }
  if (mode === 'week') {
    const from = startOfWeek(anchor);
    return { from, to: addDays(from, 7) };
  }
  if (mode === 'agenda') {
    const from = startOfDay(anchor);
    return { from, to: addDays(from, 14) };
  }
  const from = startOfDay(anchor);
  return { from, to: addDays(from, 1) };
}

function titleForRange(anchor: Date, range: { from: Date; to: Date }, mode: ViewMode) {
  if (mode === 'month') return anchor.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
  if (mode === 'day') return anchor.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  return `${range.from.toLocaleDateString('de-DE')} - ${addDays(range.to, -1).toLocaleDateString('de-DE')}`;
}

function eventsForDay(events: EventRow[], day: Date) {
  const from = startOfDay(day).getTime();
  const to = addDays(startOfDay(day), 1).getTime();
  return events.filter((e) => new Date(e.startsAt).getTime() < to && new Date(e.endsAt).getTime() > from).sort((a, b) => +new Date(a.startsAt) - +new Date(b.startsAt));
}

function eventsForHour(events: EventRow[], hour: number) {
  return events.filter((event) => event.allDay || new Date(event.startsAt).getHours() === hour);
}

function isAllDayLaneEvent(event: EventRow) {
  if (event.allDay) return true;
  const start = new Date(event.startsAt);
  const end = new Date(event.endsAt);
  return !isSameDay(start, addMinutes(end, -1));
}

function spansMultipleDays(event: EventRow) {
  const start = new Date(event.startsAt);
  const end = new Date(event.endsAt);
  return !isSameDay(start, addMinutes(end, -1));
}

function layoutMonthRangeBars(events: EventRow[], weekStart: Date): MonthRangeBarLayout[] {
  const weekEnd = addDays(weekStart, 7);
  const weekLastDay = addDays(weekStart, 6);
  const candidates = events
    .filter(spansMultipleDays)
    .filter((event) => new Date(event.startsAt).getTime() < weekEnd.getTime() && new Date(event.endsAt).getTime() > weekStart.getTime())
    .map((event) => {
      const eventStartDay = startOfDay(new Date(event.startsAt));
      const eventEndDay = startOfDay(addMinutes(new Date(event.endsAt), -1));
      const segmentStart = maxDate(eventStartDay, weekStart);
      const segmentEnd = minDate(eventEndDay, weekLastDay);
      const startIndex = daysApart(weekStart, segmentStart);
      const endIndex = daysApart(weekStart, segmentEnd);
      return {
        event,
        startIndex,
        span: Math.max(1, endIndex - startIndex + 1),
        lane: 0,
        continuesBefore: eventStartDay.getTime() < weekStart.getTime(),
        continuesAfter: eventEndDay.getTime() > weekLastDay.getTime(),
      };
    })
    .sort((a, b) => a.startIndex - b.startIndex || b.span - a.span || +new Date(a.event.startsAt) - +new Date(b.event.startsAt));

  const laneEnds: number[] = [];
  return candidates.map((bar) => {
    const endIndex = bar.startIndex + bar.span - 1;
    let lane = laneEnds.findIndex((lastEnd) => bar.startIndex > lastEnd);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(endIndex);
    } else {
      laneEnds[lane] = endIndex;
    }
    return { ...bar, lane };
  });
}

function dayIndexInBar(day: Date, weekStart: Date, bar: MonthRangeBarLayout) {
  const index = daysApart(weekStart, day);
  return index >= bar.startIndex && index < bar.startIndex + bar.span;
}

function layoutTimedEvents(events: EventRow[]) {
  const sorted = [...events].sort((a, b) => +new Date(a.startsAt) - +new Date(b.startsAt));
  const groups: EventRow[][] = [];
  for (const event of sorted) {
    const start = new Date(event.startsAt).getTime();
    const group = groups.find((items) => items.some((item) => new Date(item.startsAt).getTime() < new Date(event.endsAt).getTime() && new Date(item.endsAt).getTime() > start));
    if (group) group.push(event);
    else groups.push([event]);
  }

  return groups.flatMap((group) => {
    const columns: EventRow[][] = [];
    const laidOut: Array<{ event: EventRow; column: number; columns: number }> = [];
    for (const event of group.sort((a, b) => +new Date(a.startsAt) - +new Date(b.startsAt))) {
      const start = new Date(event.startsAt).getTime();
      let column = columns.findIndex((items) => items.every((item) => new Date(item.endsAt).getTime() <= start));
      if (column === -1) {
        column = columns.length;
        columns.push([]);
      }
      columns[column].push(event);
      laidOut.push({ event, column, columns: 1 });
    }
    const columnCount = Math.max(1, columns.length);
    return laidOut.map((item) => ({ ...item, columns: columnCount }));
  });
}

function timeEventPosition(event: EventRow, startHour: number, endHour: number, slotHeight: number, slotMinutes: number) {
  const start = new Date(event.startsAt);
  const end = new Date(event.endsAt);
  const startMinutes = start.getHours() * 60 + start.getMinutes();
  const endMinutes = end.getHours() * 60 + end.getMinutes();
  const dayStart = startHour * 60;
  const dayEnd = endHour * 60;
  const topMinutes = Math.max(dayStart, startMinutes) - dayStart;
  const visibleEndMinutes = Math.min(dayEnd, Math.max(endMinutes, startMinutes + slotMinutes));
  const heightMinutes = Math.max(slotMinutes, visibleEndMinutes - Math.max(dayStart, startMinutes));
  if (startMinutes >= dayEnd || endMinutes <= dayStart) return null;
  return {
    top: (topMinutes / slotMinutes) * slotHeight + 3,
    height: Math.max(28, (heightMinutes / slotMinutes) * slotHeight - 6),
  };
}

function timeRange(event: EventRow) {
  if (event.allDay) return 'ganztags';
  return `${new Date(event.startsAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} - ${new Date(event.endsAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;
}

function relativeDay(value: string) {
  const day = startOfDay(new Date(value));
  const today = startOfDay(new Date());
  const diff = Math.round((day.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
  if (diff === 0) return 'heute';
  if (diff === 1) return 'morgen';
  if (diff === -1) return 'gestern';
  return day.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
}

function totalDurationHours(events: EventRow[]) {
  const minutes = events.reduce((sum, event) => {
    if (event.allDay) return sum + 8 * 60;
    const start = new Date(event.startsAt).getTime();
    const end = new Date(event.endsAt).getTime();
    return sum + Math.max(0, Math.round((end - start) / 60000));
  }, 0);
  return Math.round((minutes / 60) * 10) / 10;
}

function busiestDay(days: Date[], events: EventRow[]) {
  const ranked = days
    .map((day) => ({ day, count: eventsForDay(events, day).length }))
    .sort((a, b) => b.count - a.count);
  const best = ranked[0];
  if (!best || best.count === 0) return { label: 'ruhig', count: 0 };
  return {
    label: best.day.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit' }),
    count: best.count,
  };
}

function eventBackground(color: string) {
  const hex = color.replace('#', '');
  if (!/^[0-9a-f]{6}$/i.test(hex)) return 'rgba(255,255,255,0.07)';
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `linear-gradient(135deg, rgba(${r},${g},${b},0.25), rgba(${r},${g},${b},0.09))`;
}

function initialQuickForm(date: Date): QuickForm {
  const start = roundToNextQuarter(date);
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return {
    calendarId: '',
    summary: '',
    location: '',
    startsAt: toLocalInput(start),
    endsAt: toLocalInput(end),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    allDay: false,
    recurrence: 'NONE',
    recurrenceInterval: '1',
    recurrenceByDay: [weekdayCode(start)],
    recurrenceMonthlyMode: 'DATE',
    recurrenceYearlyMode: 'DATE',
    recurrenceMonthDay: String(start.getDate()),
    recurrenceBySetPos: String(ordinalInMonth(start)),
    recurrenceByWeekday: weekdayCode(start),
    recurrenceByMonth: String(start.getMonth() + 1),
    recurrenceEndMode: 'NEVER',
    recurrenceCount: '10',
    recurrenceUntil: toLocalDateInput(addDays(startOfDay(start), 30)),
    sendInvitations: true,
  };
}

function recurrenceToRrule(form: QuickForm): string | null {
  if (form.recurrence === 'NONE') return null;
  const parts = [`FREQ=${form.recurrence}`];
  const interval = Math.max(1, Math.min(999, Number(form.recurrenceInterval) || 1));
  if (interval > 1) parts.push(`INTERVAL=${interval}`);
  if (form.recurrence === 'WEEKLY' && form.recurrenceByDay.length > 0) {
    parts.push(`BYDAY=${form.recurrenceByDay.join(',')}`);
  }
  if (form.recurrence === 'MONTHLY') {
    if (form.recurrenceMonthlyMode === 'WEEKDAY') {
      parts.push(`BYDAY=${form.recurrenceByWeekday}`);
      parts.push(`BYSETPOS=${normalizeSetPos(form.recurrenceBySetPos)}`);
    } else {
      parts.push(`BYMONTHDAY=${normalizeMonthDay(form.recurrenceMonthDay)}`);
    }
  }
  if (form.recurrence === 'YEARLY') {
    parts.push(`BYMONTH=${normalizeMonth(form.recurrenceByMonth)}`);
    if (form.recurrenceYearlyMode === 'WEEKDAY') {
      parts.push(`BYDAY=${form.recurrenceByWeekday}`);
      parts.push(`BYSETPOS=${normalizeSetPos(form.recurrenceBySetPos)}`);
    } else {
      parts.push(`BYMONTHDAY=${normalizeMonthDay(form.recurrenceMonthDay)}`);
    }
  }
  if (form.recurrenceEndMode === 'COUNT') {
    const count = Math.max(1, Math.min(999, Number(form.recurrenceCount) || 1));
    parts.push(`COUNT=${count}`);
  }
  if (form.recurrenceEndMode === 'UNTIL' && form.recurrenceUntil) {
    parts.push(`UNTIL=${rruleUntil(form.recurrenceUntil)}`);
  }
  return parts.join(';');
}

function weekdayCode(date: Date) {
  return ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'][date.getDay()];
}

function ordinalInMonth(date: Date) {
  const ordinal = Math.ceil(date.getDate() / 7);
  return ordinal > 4 ? -1 : ordinal;
}

function normalizeMonthDay(value: string) {
  return Math.max(1, Math.min(31, Number(value) || 1));
}

function normalizeMonth(value: string) {
  return Math.max(1, Math.min(12, Number(value) || 1));
}

function normalizeSetPos(value: string) {
  const allowed = new Set(['1', '2', '3', '4', '-1']);
  return allowed.has(value) ? value : '1';
}

function recurrenceUnitLabel(value: RecurrenceFrequency) {
  if (value === 'DAILY') return 'Tag(e)';
  if (value === 'WEEKLY') return 'Woche(n)';
  if (value === 'MONTHLY') return 'Monat(e)';
  if (value === 'YEARLY') return 'Jahr(e)';
  return 'Einheiten';
}

function rruleUntil(value: string) {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value.replace(/-/g, '');
  const until = new Date(Date.UTC(year, month - 1, day, 23, 59, 59));
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${until.getUTCFullYear()}${pad(until.getUTCMonth() + 1)}${pad(until.getUTCDate())}T${pad(until.getUTCHours())}${pad(until.getUTCMinutes())}${pad(until.getUTCSeconds())}Z`;
}

function orderedDays(a: Date, b: Date): [Date, Date] {
  const start = startOfDay(a);
  const end = startOfDay(b);
  return start.getTime() <= end.getTime() ? [start, end] : [end, start];
}

function orderedDateRange(a: Date, b: Date): { start: Date; end: Date } {
  return a.getTime() <= b.getTime() ? { start: a, end: b } : { start: b, end: a };
}

function isDayInSelection(day: Date, start: Date, end: Date) {
  const [from, to] = orderedDays(start, end);
  const stamp = startOfDay(day).getTime();
  return stamp >= from.getTime() && stamp <= to.getTime();
}

function toLocalDateInput(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function dateFromLocalInput(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
}

function dateFromLocalDateTimeInput(value: string): Date {
  const [date, time = '00:00'] = value.split('T');
  const [year, month, day] = date.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);
  return new Date(year, (month || 1) - 1, day || 1, hour || 0, minute || 0, 0, 0);
}

function addMinutes(d: Date, minutes: number) {
  return new Date(d.getTime() + minutes * 60 * 1000);
}

function parseAttendees(input: string): AttendeeDraft[] {
  return input
    .split(/[\n,;]/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const m = line.match(/^(.+?)\s*<(.+@.+)>$/);
      if (m) return { name: m[1].trim(), email: m[2].trim().toLowerCase() };
      return { email: line.toLowerCase() };
    })
    .filter((a) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.email));
}

function parseReminders(input: string) {
  return input
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => {
      const [minutes, channel] = item.split(':');
      return { minutesBefore: Number(minutes), channel: channel === 'EMAIL' ? 'EMAIL' : 'NOTIFICATION' };
    })
    .filter((item) => Number.isFinite(item.minutesBefore));
}

function formatReminder(minutes: number) {
  if (minutes === 0) return 'zum Start';
  if (minutes < 60) return `${minutes} Min. vorher`;
  if (minutes % 1440 === 0) return `${minutes / 1440} Tag(e) vorher`;
  if (minutes % 60 === 0) return `${minutes / 60} Std. vorher`;
  return `${minutes} Min. vorher`;
}

function toggleSet(prev: Set<string>, value: string) {
  const next = new Set(prev);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

function toggleArray(values: string[], value: string) {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

function modeLabel(mode: ViewMode) {
  if (mode === 'month') return 'Monat';
  if (mode === 'week') return 'Woche';
  if (mode === 'day') return 'Tag';
  return 'Agenda';
}

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function startOfWeek(d: Date) {
  const day = (d.getDay() + 6) % 7;
  return addDays(startOfDay(d), -day);
}
function addDays(d: Date, days: number) {
  const out = new Date(d);
  out.setDate(out.getDate() + days);
  return out;
}
function shift(d: Date, mode: ViewMode, amount: number) {
  const out = new Date(d);
  if (mode === 'month') out.setMonth(out.getMonth() + amount);
  else out.setDate(out.getDate() + (mode === 'week' ? 7 : mode === 'agenda' ? 14 : 1) * amount);
  return out;
}
function daysBetween(from: Date, to: Date) {
  const days: Date[] = [];
  for (let d = startOfDay(from); d < to; d = addDays(d, 1)) days.push(d);
  return days;
}
function daysApart(from: Date, to: Date) {
  return Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / (24 * 60 * 60 * 1000));
}
function maxDate(a: Date, b: Date) {
  return a.getTime() > b.getTime() ? a : b;
}
function minDate(a: Date, b: Date) {
  return a.getTime() < b.getTime() ? a : b;
}
function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function roundToNextQuarter(d: Date): Date {
  const out = new Date(d);
  out.setMinutes(Math.ceil(out.getMinutes() / 15) * 15);
  out.setSeconds(0);
  out.setMilliseconds(0);
  return out;
}
function toLocalInput(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
